import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, XCircle, ExternalLink } from 'lucide-react';
import { EndorsementsList } from '../components/pas/TreasuryCheckEndorse';
import { BankLookupPanel } from '../components/pas/BankLookupPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-treasury-check/public/checks`;
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const who = (p) => (p ? [...new Set([p.name, p.email, p.wallet].filter(Boolean))].join(' · ') : '—');

const Row = ({ k, v, mono, testid }) => (
  <div className="py-2 border-b border-slate-700 grid sm:grid-cols-3 gap-2">
    <p className="text-xs uppercase tracking-wide text-slate-500">{k}</p>
    <p className={`sm:col-span-2 text-sm text-white [overflow-wrap:anywhere] ${mono ? 'font-mono text-xs' : ''}`} data-testid={testid}>{v || '—'}</p>
  </div>
);

export default function VerifyTreasuryCheck() {
  const { checkId } = useParams();
  const [params] = useSearchParams();
  const [c, setC] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    axios.get(`${API}/${checkId}`, { params: { t: params.get('t') || '' } }).then(r => setC(r.data)).catch(() => setError('This Treasury Check could not be verified.'));
  }, [checkId, params]);
  if (error) return <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center gap-3 text-red-400" data-testid="tc-verify-error"><XCircle className="w-12 h-12" />{error}</div>;
  if (!c) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400">Verifying...</div>;
  const p = c.payload || {};
  const r = p.treasury_reference || {};
  const sym = c.token_symbol === 'BETH' ? 'BETH' : 'BTCK';
  const sc = p.services_contract_award || {};
  const parties = p.parties || {};
  return (
    <div className="min-h-screen bg-slate-900 px-4 py-10" data-testid="tc-verify-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6 flex items-center gap-4">
          <ShieldCheck className="w-10 h-10 text-emerald-400 shrink-0" />
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="tc-verify-title">Treasury Check No. {c.check_no} · {usd(c.amount)}</h1>
            <p className="text-sm text-emerald-300">Issued on {{ mainnet: 'Ethereum mainnet', base: 'Base', arbitrum: 'Arbitrum' }[c.network] || 'Sepolia'} · {c.status} · verified {new Date(c.verified_at).toLocaleString()}</p>
          </div>
        </div>
        {c.upgraded && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl px-6 py-4 text-sm text-emerald-200" data-testid="verify-upgraded-notice">
            <strong>Upgraded to BETH USD.</strong> {c.upgraded.note}{' '}
            <a href={c.upgraded.explorer_url} target="_blank" rel="noreferrer" className="underline">View stablecoin contract</a>
          </div>
        )}
        <div className="rounded-2xl overflow-hidden border border-slate-700 empty:hidden"><BankLookupPanel params={params} /></div>
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
          <h2 className="text-white font-semibold mb-2">Check facts</h2>
          <Row k="Amount" v={`${usd(c.amount)} (${c.amount_words})`} testid="tc-verify-amount" />
          <Row k="Issued" v={c.date} />
          <Row k="Treasury reference" v={`${r.cusip} · ${r.security_term} · reopening ${r.auction_date}`} testid="tc-verify-cusip" />
          <Row k="Reference data hash" v={r.reference_hash} mono />
          <Row k="Payee" v={who(c.payee)} />
          <Row k="Current holder" v={who(c.current_holder_party) || c.current_holder || c.payee_address} testid="tc-verify-holder" />
          <Row k={`Holder ${sym} balance`} v={c.holder_balance != null ? `${c.holder_balance} ${sym}` : 'unavailable'} testid="tc-verify-balance" />
          <Row k="On-chain issue" v={<a href={c.explorer_url} target="_blank" rel="noreferrer" className="text-emerald-400 inline-flex items-center gap-1 underline">View transaction <ExternalLink className="w-3 h-3" /></a>} testid="tc-verify-tx" />
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6" data-testid="tc-verify-payload">
          <h2 className="text-white font-semibold mb-2">Payload transmittal ({p.version})</h2>
          <Row k="Services contract" v={[sc.contract_no, sc.description].filter(Boolean).join(' · ')} />
          <Row k="Award" v={sc.award_amount_usd != null ? `${usd(sc.award_amount_usd)}${sc.award_date ? ` · ${sc.award_date}` : ''}` : sc.award_date} />
          <Row k="Buyer" v={who(parties.buyer)} /><Row k="Seller" v={who(parties.seller)} />
          <Row k="Transferee" v={who(parties.transferee)} /><Row k="Assignee" v={who(parties.assignee)} />
          <Row k="Funding" v={p.funding} /><Row k="Memo" v={p.memo} />
          <Row k="Payload hash" v={c.payload_hash} mono testid="tc-verify-payload-hash" />
          <Row k="Collateral snapshot" v={p.collateral_hash} mono />
          <Row k="Contract" v={c.contract} mono />
        </div>
        <EndorsementsList items={c.endorsements || []} />
        <p className="text-xs text-slate-500">{c.redemption_statement}</p>
      </div>
    </div>
  );
}
